import { useLanguage } from "@/contexts/LanguageContext";
import { Server, Workflow, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";

const SolutionsSection = () => {
  const { t } = useLanguage();

  const solutions = [
    {
      icon: <Server className="w-8 h-8 text-blue-600 dark:text-blue-400" />,
      title: t("onprem_ai_title"),
      description: t("onprem_ai_desc"),
      features: [
        t("feature_private_ai"),
        t("feature_secure_chatbots"),
        t("feature_rag"),
        t("feature_doc_analysis")
      ],
      gradient: "from-blue-600/5 via-blue-500/5 to-transparent",
      buttonText: t("deploy_ai")
    },
    {
      icon: <Workflow className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />,
      title: t("automation_title"),
      description: t("automation_desc"),
      features: [
        t("feature_ai_workflows"),
        t("feature_microsoft"),
        t("feature_pipelines"),
        t("feature_custom_workflows")
      ],
      gradient: "from-emerald-600/5 via-emerald-500/5 to-transparent",
      buttonText: t("explore_automation")
    },
    {
      icon: <Shield className="w-8 h-8 text-blue-500 dark:text-blue-400" />,
      title: t("infrastructure_title"),
      description: t("infrastructure_desc"),
      features: [
        t("feature_hosting"),
        t("feature_docker"),
        t("feature_encryption"),
        t("feature_architecture")
      ],
      gradient: "from-blue-500/5 via-blue-400/5 to-transparent",
      buttonText: t("assess_readiness")
    }
  ];

  return (
    <section id="solutions" className="relative overflow-hidden py-24 sm:py-28">
      <div className="absolute inset-0 z-0">
        <div className="absolute right-1/4 top-1/4 h-96 w-96 rounded-full bg-blue-500/5 blur-[120px] dark:bg-blue-600/5"></div>
        <div className="absolute bottom-1/4 left-1/4 h-96 w-96 rounded-full bg-slate-500/5 blur-[120px] dark:bg-slate-600/5"></div>
      </div>

      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mx-auto mb-14 max-w-4xl text-center"
        >
          <h2 className="section-heading mb-6">
            {t("solutions_title")}
          </h2>
          <p className="section-subtitle mx-auto max-w-3xl">
            {t("solutions_subtitle")}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.15 }}
          className="mx-auto grid max-w-7xl gap-6 lg:grid-cols-3"
        >
          {solutions.map((solution, index) => (
            <div
              key={index}
              className="soft-card group relative overflow-hidden"
            >
              <div className={`absolute inset-0 bg-gradient-to-br ${solution.gradient} opacity-0 transition-opacity duration-300 group-hover:opacity-100`} />
              <div className="relative">
                <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100/90 dark:bg-slate-900/90">
                  {solution.icon}
                </div>
                <h3 className="mb-3 text-xl font-semibold text-slate-900 transition-colors group-hover:text-blue-600 dark:text-white dark:group-hover:text-blue-400">
                  {solution.title}
                </h3>
                <p className="mb-6 text-sm leading-7 text-slate-600 dark:text-slate-400">
                  {solution.description}
                </p>
                <ul className="mb-6 space-y-3">
                  {solution.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start">
                      <span className="mr-2 text-blue-600 dark:text-blue-400">•</span>
                      <span className="text-sm leading-7 text-slate-600 dark:text-slate-400">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  variant="outline"
                  className="w-full rounded-full border border-slate-200 bg-white/80 text-slate-800 transition-all duration-300 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900/70 dark:text-slate-200 dark:hover:bg-slate-900"
                  onClick={() => {
                    if (solution.buttonText === t("training_title") || solution.title === t("training_title")) {
                      document.getElementById("training")?.scrollIntoView({ behavior: "smooth" });
                      setTimeout(() => {
                        (window as any).toast && (window as any).toast({
                          title: t("training_title"),
                          description: t("training_desc"),
                        });
                      }, 700);
                    } else {
                      document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
                      setTimeout(() => {
                        (window as any).toast && (window as any).toast({
                          title: solution.buttonText,
                          description: t("well_be_in_touch"),
                        });
                      }, 700);
                    }
                  }}
                >
                  {solution.buttonText}
                </Button>
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default SolutionsSection;
